import type { SortDirection, SortKey } from '@mnemo/shared';
import type { TranslationKey } from '../i18n.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Les deux gestes du tableau.
 *
 * Plutôt que sept menus déroulants dans les en-têtes — inutilisables sur les colonnes à
 * forte cardinalité, 2 300 utilisateurs et 83 appareils — on offre deux gestes qui, eux,
 * passent à l'échelle :
 *
 *   · **trier** en cliquant l'en-tête, re-cliquer inverse le sens ;
 *   · **filtrer** en cliquant la valeur elle-même, dans la cellule.
 *
 * Le second est le geste naturel devant un journal : je vois `PRTEPCC083-00`, je veux
 * cet appareil. Un clic, et aucune liste à dérouler.
 */

/** En-tête triable. Le `aria-sort` du `th` est ce que lit un lecteur d'écran. */
export function SortableHead({
  t,
  label,
  column,
  sort,
  dir,
  onSort,
  align = 'left',
  className = '',
}: {
  t: T;
  label: string;
  column: SortKey;
  sort: SortKey;
  dir: SortDirection;
  onSort: (column: SortKey) => void;
  align?: 'left' | 'right';
  /** Pour masquer la colonne sur petit écran, comme ses cellules (VUE-08). */
  className?: string;
}) {
  const active = sort === column;
  return (
    <th
      scope="col"
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        title={t('sort.by', { column: label })}
        className={`inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs uppercase tracking-wide transition-colors hover:text-fg ${
          active ? 'font-bold text-accent' : 'text-muted'
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {label}
        {/* Jamais la couleur seule : la flèche dit le sens, et le libellé change de graisse. */}
        <span aria-hidden="true" className={active ? '' : 'opacity-0 group-hover:opacity-40'}>
          {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * Une valeur cliquable : un clic pose le filtre correspondant.
 *
 * Quand le filtre est déjà posé, la valeur cesse d'être un bouton — proposer de filtrer
 * sur ce qui est déjà filtré ne mènerait nulle part.
 */
export function FilterableValue({
  t,
  value,
  active,
  onFilter,
  className = '',
}: {
  t: T;
  value: string | null;
  active: boolean;
  onFilter: (value: string) => void;
  className?: string;
}) {
  if (!value) return <span className="text-muted">—</span>;
  if (active) return <span className={className}>{value}</span>;
  return (
    <button
      type="button"
      onClick={() => onFilter(value)}
      title={t('filter.clickTo', { value })}
      className={`rounded px-1 text-left underline-offset-2 transition-colors hover:text-accent hover:underline ${className}`}
    >
      {value}
    </button>
  );
}

/**
 * La barre des filtres actifs.
 *
 * Avec neuf filtres possibles, « pourquoi si peu de lignes ? » devient une vraie question.
 * Chaque filtre posé est donc dit en toutes lettres, et se retire d'un clic. C'est le
 * pendant des « états vides dits en toutes lettres » : un tableau filtré ne doit jamais
 * pouvoir passer pour un tableau vide.
 */
export function ActiveFilters({
  t,
  filters,
  onRemove,
  onClear,
}: {
  t: T;
  filters: { key: string; label: string; value: string }[];
  onRemove: (key: string) => void;
  onClear: () => void;
}) {
  if (filters.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 px-1">
      <span className="text-xs uppercase tracking-wide text-muted">{t('filter.active')}</span>
      {filters.map((f) => (
        <span
          key={f.key}
          className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 py-0.5 pl-2.5 pr-1 text-xs"
        >
          <span className="text-muted">{f.label}</span>
          <span className="font-semibold">{f.value}</span>
          <button
            type="button"
            onClick={() => onRemove(f.key)}
            aria-label={t('filter.remove', { label: f.label, value: f.value })}
            title={t('filter.remove', { label: f.label, value: f.value })}
            className="flex h-6 w-6 items-center justify-center rounded-full text-muted transition-colors hover:bg-accent/20 hover:text-fg"
          >
            <span aria-hidden="true">×</span>
          </button>
        </span>
      ))}
      {filters.length > 1 && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-muted underline hover:text-fg"
        >
          {t('filter.clearAll')}
        </button>
      )}
    </div>
  );
}
